/**
 * Subscription Domain Types
 * 
 * Responsabilidade: Definir entidades e tipos para o domínio de assinaturas.
 * Segue princípio de Single Source of Truth para tipos de planos e status.
 */

// =============================================================================
// TIPOS DE PLANO E STATUS
// =============================================================================

/**
 * IDs dos planos disponíveis (devem corresponder ao banco)
 */
export type PlanId = 'basic' | 'pro' | 'enterprise';

/**
 * Status da assinatura no Stripe/Sistema
 */
export type SubscriptionStatus =
    | 'trialing'    // Em período de trial
    | 'active'      // Assinatura ativa e paga
    | 'past_due'    // Pagamento atrasado (graça de 5 dias)
    | 'canceled'    // Cancelada pelo usuário
    | 'incomplete'  // Pagamento inicial falhou
    | 'blocked';    // Bloqueado por inadimplência (>5 dias)

// =============================================================================
// ENTIDADES
// =============================================================================

/**
 * Plano de assinatura (espelho da tabela subscription_plans)
 */
export interface SubscriptionPlan {
    id: PlanId;
    name: string;
    pricePerUser: number;
    minUsers: number;
    maxFunnels: number;      // -1 = ilimitado
    maxContacts: number;     // -1 = ilimitado
    maxWhatsapp: number;
    maxAdAccounts: number;
    features: PlanFeatures;
    stripePriceId: string | null;
    isActive: boolean;
}

/**
 * Features disponíveis por plano
 */
export interface PlanFeatures {
    metaTracking: boolean;
    autoMovement: boolean;
    instagramDm?: 'available' | 'coming_soon' | 'unavailable';
    instagramTracking?: 'available' | 'coming_soon' | 'unavailable';
    googleAds?: 'available' | 'coming_soon' | 'unavailable';
    displayItems?: string[];
}

/**
 * Estado da assinatura de uma organização
 */
export interface OrganizationSubscription {
    planId: PlanId;
    status: SubscriptionStatus;
    stripeCustomerId: string | null;
    stripeSubscriptionId: string | null;
    quantity: number;           // Número de usuários/assentos
    trialEndsAt: Date | null;
    blockedAt: Date | null;
}

// =============================================================================
// DTOs PARA API
// =============================================================================

/**
 * DTO para criar sessão de checkout
 */
export interface CreateCheckoutDTO {
    organizationId: string;
    planId: PlanId;
    quantity: number;
    successUrl: string;
    cancelUrl: string;
}

/**
 * Resposta da criação de checkout
 */
export interface CheckoutSessionResponse {
    sessionId?: string;
    url?: string;
    synced?: boolean;
    alreadySubscribed?: boolean;
    plan?: string;
    subscriptionStatus?: string;
}

/**
 * DTO para atualizar assinatura (via webhook)
 */
export interface UpdateSubscriptionDTO {
    stripeCustomerId: string;
    stripeSubscriptionId: string;
    status: SubscriptionStatus;
    planId: PlanId;
    quantity: number;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Labels e descrições dos planos (fonte única — UI não duplica)
 */
export const PLAN_LABELS: Record<PlanId, string> = {
    basic: 'Básico',
    pro: 'Pro',
    enterprise: 'Enterprise',
};

const PLAN_DESCRIPTIONS: Record<PlanId, string> = {
    basic: 'Para pequenos negócios começando',
    pro: 'Para equipes em crescimento',
    enterprise: 'Para grandes operações',
};

export function getPlanLabel(planId: string): string {
    return PLAN_LABELS[planId as PlanId] ?? planId;
}

export function getPlanDescription(planId: string): string {
    return PLAN_DESCRIPTIONS[planId as PlanId] ?? '';
}

/** Preços atuais e anteriores. O id do Stripe não traz o nome do plano. */
const PLAN_PRICE_IDS: Record<string, PlanId> = {
    price_1UNYauI74cGc1RUIOzG9SKY3: 'basic',
    price_1UNYauI74cGc1RUIjuPCQcRx: 'pro',
    price_1UNYauI74cGc1RUIS2TUsa04: 'enterprise',
    price_1SqDBeI74cGc1RUIPw2KKV7Y: 'basic',
    price_1SqjRbI74cGc1RUIZ4yzT2Tq: 'pro',
    price_1SqjJ8I74cGc1RUI8peek7cy: 'enterprise',
};

export function resolvePlanIdFromPrice(
    priceId: string,
    metadata?: Record<string, string> | null,
    envPriceToPlan?: Record<string, string>,
): string {
    const fromMetadata = metadata?.plan_id;
    if (fromMetadata && fromMetadata in PLAN_LABELS) return fromMetadata;

    if (priceId && PLAN_PRICE_IDS[priceId]) return PLAN_PRICE_IDS[priceId];
    if (priceId && envPriceToPlan?.[priceId]) return envPriceToPlan[priceId];

    if (priceId.includes('enterprise')) return 'enterprise';
    if (priceId.includes('pro')) return 'pro';
    if (priceId.includes('basic')) return 'basic';

    return 'basic';
}

/**
 * Verifica se o plano permite a feature
 */
export function planHasFeature(plan: SubscriptionPlan, feature: keyof PlanFeatures): boolean {
    const value = plan.features[feature];
    if (typeof value === 'boolean') return value;
    return value === 'available';
}

/**
 * Verifica se o limite é ilimitado
 */
export function isUnlimited(limit: number): boolean {
    return limit === -1;
}

/**
 * Calcula preço mensal total
 */
export function calculateMonthlyPrice(plan: SubscriptionPlan, quantity: number): number {
    const users = Math.max(quantity, plan.minUsers);
    return plan.pricePerUser * users;
}

/**
 * Verifica se a organização está bloqueada
 */
export function isSubscriptionBlocked(subscription: OrganizationSubscription): boolean {
    // Status explicitamente bloqueado
    if (subscription.status === 'blocked') return true;

    // Past due por mais de 5 dias
    if (subscription.status === 'past_due' && subscription.blockedAt) {
        return new Date() >= subscription.blockedAt;
    }

    // Trial expirado
    if (subscription.status === 'trialing' && subscription.trialEndsAt) {
        return new Date() > subscription.trialEndsAt;
    }

    return false;
}

/**
 * Dias restantes no trial
 */
export function getTrialDaysRemaining(trialEndsAt: Date | null): number {
    if (!trialEndsAt) return 0;
    const now = new Date();
    const diff = trialEndsAt.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

/** Status que ainda cobram / bloqueiam segundo checkout no Stripe. */
export const BILLABLE_STRIPE_STATUSES: readonly SubscriptionStatus[] = [
    'active',
    'trialing',
    'past_due',
];

/**
 * Trial local (org.subscription_status=trialing sem stripe_subscription_id)
 * NÃO é assinatura Stripe — ainda precisa de Checkout para pagar.
 */
export function hasBillableStripeSubscription(
    subscription: Pick<OrganizationSubscription, 'stripeSubscriptionId' | 'status'> | null | undefined,
): boolean {
    if (!subscription?.stripeSubscriptionId) return false;
    return BILLABLE_STRIPE_STATUSES.includes(subscription.status);
}
